import { useMutation, useQueryClient } from '@tanstack/react-query';

import OffersService from '~/services/OffersService';

import { offerQueryKeys } from './useGetOffer';

interface ToggleParams {
  schedules: Array<App.Schedule>;
  restrict: boolean;
}

export function useToggleLuxPlusRestriction(offerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ schedules, restrict }: ToggleParams) => {
      if (!offerId) {
        throw new Error('offerId is required');
      }

      const results = await Promise.allSettled(
        schedules.map((schedule) =>
          OffersService.updateSchedule(
            {
              start: new Date(schedule.start).toISOString(),
              end: new Date(schedule.end).toISOString(),
              region: schedule.region,
              brand: schedule.brand,
              type: schedule.type,
              restrict_purchase_to_lux_plus_members: restrict,
            },
            offerId,
            schedule.id,
          ),
        ),
      );

      const failureCount = results.filter((result) => result.status === 'rejected').length;
      if (failureCount > 0) {
        throw new Error(`Failed to update ${failureCount} of ${schedules.length} schedules`);
      }
    },
    // onSettled, not onSuccess: Promise.allSettled above can partially succeed and still throw,
    // so the cache needs invalidating even on failure or the successfully-patched rows go stale.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.detail(offerId) });
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.scheduleLogs(offerId) });
    },
  });
}
